import type { PlanStep, StepResult } from '@verifyloop/core';
import { StepStatusPill, StepTypePill } from './Pills';
import { formatDuration } from '../lib/format';

export interface PlaybookFlowProps {
  steps: readonly PlanStep[];
  stepResults?: readonly StepResult[];
  selectedStepIndex: number | null;
  onSelectStep: (index: number) => void;
  activeRunningIndex?: number | null;
}

// --------------------------------------------------------------------------------
// VARIANT A: Horizontal Pipeline Flow with Directed SVG Connectors
// --------------------------------------------------------------------------------
export function VariantA_PipelineFlow({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
}: PlaybookFlowProps): JSX.Element {
  if (steps.length === 0) {
    return <div className="text-muted p-4 text-center">No steps in this plan.</div>;
  }

  return (
    <div className="playbook-flow playbook-flow--pipeline" style={{ padding: '16px 0', overflowX: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', minWidth: 'max-content', padding: '0 8px' }}>
        {steps.map((step, index) => {
          const result = stepResults?.[index];
          const isSelected = selectedStepIndex === index;
          const isRunning = activeRunningIndex === index;
          const status = isRunning ? 'running' : result?.status ?? 'pending';

          const statusColor =
            status === 'passed'
              ? '#10b981'
              : status === 'failed'
                ? '#ef4444'
                : status === 'running'
                  ? '#3b82f6'
                  : '#64748b';

          return (
            <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
              {/* Step Card Node */}
              <button
                type="button"
                onClick={() => onSelectStep(index)}
                aria-pressed={isSelected}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 8,
                  width: 220,
                  textAlign: 'left',
                  background: isSelected ? 'var(--bg-panel)' : 'var(--bg-inset)',
                  border: `2px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
                  borderLeft: `4px solid ${statusColor}`,
                  borderRadius: 8,
                  padding: '12px 14px',
                  cursor: 'pointer',
                  boxShadow: isSelected
                    ? '0 0 0 3px rgba(59, 130, 246, 0.25), 0 4px 12px rgba(0, 0, 0, 0.3)'
                    : '0 2px 6px rgba(0, 0, 0, 0.15)',
                  transition: 'all 0.15s ease',
                  position: 'relative',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'var(--text-muted, #94a3b8)',
                        background: 'rgba(255, 255, 255, 0.06)',
                        padding: '1px 5px',
                        borderRadius: 4,
                      }}
                    >
                      #{index + 1}
                    </span>
                    <StepTypePill type={step.type} />
                  </div>
                  {result ? (
                    <StepStatusPill status={result.status} />
                  ) : isRunning ? (
                    <span
                      style={{
                        fontSize: 11,
                        padding: '1px 6px',
                        borderRadius: 9999,
                        background: '#1d4ed8',
                        color: '#fff',
                        animation: 'pulse 1.5s infinite',
                      }}
                    >
                      running
                    </span>
                  ) : null}
                </div>

                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    lineHeight: 1.35,
                    color: 'var(--text)',
                    wordBreak: 'break-word',
                  }}
                >
                  {step.description}
                </div>

                {step.target ? (
                  <div
                    style={{
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: 'var(--text-muted)',
                      background: 'var(--bg-inset)',
                      padding: '2px 6px',
                      borderRadius: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={step.target}
                  >
                    {step.target}
                  </div>
                ) : null}

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    fontSize: 11,
                    color: 'var(--text-muted, #94a3b8)',
                    marginTop: 2,
                  }}
                >
                  {result?.durationMs !== undefined ? (
                    <span>{formatDuration(result.durationMs)}</span>
                  ) : step.durationMs !== undefined ? (
                    <span>~{formatDuration(step.durationMs)}</span>
                  ) : (
                    <span />
                  )}

                  {result && result.artifacts && result.artifacts.length > 0 ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#38bdf8' }}>
                      📸 {result.artifacts.length}
                    </span>
                  ) : null}
                </div>
              </button>

              {/* Connecting Arrow to Next Step */}
              {index < steps.length - 1 ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '0 8px',
                    color: isSelected ? '#3b82f6' : 'var(--border, #64748b)',
                  }}
                  aria-hidden="true"
                >
                  <svg width="28" height="24" viewBox="0 0 28 24" fill="none">
                    <line
                      x1="0"
                      y1="12"
                      x2="20"
                      y2="12"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeDasharray={result ? undefined : '3 3'}
                    />
                    <polygon points="18,7 26,12 18,17" fill="currentColor" />
                  </svg>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------------
// VARIANT B: Vertical Directed Flowchart with Central Spine & Metrics
// --------------------------------------------------------------------------------
export function VariantB_VerticalFlowchart({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
}: PlaybookFlowProps): JSX.Element {
  if (steps.length === 0) {
    return <div className="text-muted p-4 text-center">No steps in this plan.</div>;
  }

  return (
    <div className="playbook-flow playbook-flow--vertical" style={{ padding: '12px 16px', maxWidth: 640 }}>
      {steps.map((step, index) => {
        const result = stepResults?.[index];
        const isSelected = selectedStepIndex === index;
        const isRunning = activeRunningIndex === index;
        const isLast = index === steps.length - 1;
        const status = isRunning ? 'running' : result?.status ?? 'pending';

        const statusIcon =
          status === 'passed' ? '✓' : status === 'failed' ? '✕' : status === 'running' ? '⟳' : `${index + 1}`;

        const statusBg =
          status === 'passed'
            ? '#10b981'
            : status === 'failed'
              ? '#ef4444'
              : status === 'running'
                ? '#3b82f6'
                : 'var(--border)';

        return (
          <div key={index} style={{ display: 'flex', gap: 14, position: 'relative' }}>
            {/* Timeline Spine & Node Icon */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 28 }}>
              <div
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 9999,
                  background: statusBg,
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  boxShadow: isSelected ? '0 0 0 3px rgba(59, 130, 246, 0.4)' : undefined,
                  zIndex: 2,
                }}
              >
                {statusIcon}
              </div>

              {!isLast ? (
                <div
                  style={{
                    width: 2,
                    flex: 1,
                    minHeight: 32,
                    background: result ? 'var(--border)' : 'var(--border)',
                    borderLeft: result ? undefined : '2px dashed var(--border)',
                    margin: '4px 0',
                    zIndex: 1,
                  }}
                />
              ) : null}
            </div>

            {/* Step Card */}
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : 16 }}>
              <button
                type="button"
                onClick={() => onSelectStep(index)}
                aria-pressed={isSelected}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: isSelected ? 'var(--bg-panel)' : 'var(--bg-inset)',
                  border: `1.5px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
                  borderRadius: 8,
                  padding: '10px 14px',
                  cursor: 'pointer',
                  boxShadow: isSelected ? '0 0 0 2px rgba(59, 130, 246, 0.2)' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <StepTypePill type={step.type} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                      {step.description}
                    </span>
                  </div>
                  {result ? <StepStatusPill status={result.status} /> : null}
                </div>

                {step.target ? (
                  <div
                    style={{
                      fontSize: 11,
                      fontFamily: 'monospace',
                      color: 'var(--text-muted, #94a3b8)',
                    }}
                  >
                    target: {step.target}
                  </div>
                ) : null}

                {result?.error ? (
                  <div
                    style={{
                      fontSize: 12,
                      color: '#ef4444',
                      background: 'rgba(239, 68, 68, 0.1)',
                      padding: '4px 8px',
                      borderRadius: 4,
                    }}
                  >
                    {result.error}
                  </div>
                ) : null}

                {result?.durationMs !== undefined ? (
                  <div style={{ fontSize: 11, color: 'var(--text-muted, #94a3b8)', alignSelf: 'flex-end' }}>
                    {formatDuration(result.durationMs)}
                  </div>
                ) : null}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// --------------------------------------------------------------------------------
// VARIANT C: Phased Stage Canvas (Setup -> Interaction -> Verification)
// --------------------------------------------------------------------------------
export function VariantC_PhasedStages({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
}: PlaybookFlowProps): JSX.Element {
  if (steps.length === 0) {
    return <div className="text-muted p-4 text-center">No steps in this plan.</div>;
  }

  // Partition steps into semantic phases
  const stages: { title: string; indices: number[] }[] = [
    { title: '1. Setup & Navigation', indices: [] },
    { title: '2. User Actions', indices: [] },
    { title: '3. Verification & Assertions', indices: [] },
  ];

  steps.forEach((step, idx) => {
    if (step.type === 'navigation' || step.type === 'setup') {
      stages[0]?.indices.push(idx);
    } else if (step.type === 'assertion' || step.type === 'extract' || step.type === 'teardown') {
      stages[2]?.indices.push(idx);
    } else {
      stages[1]?.indices.push(idx);
    }
  });

  // Filter non-empty stages
  const activeStages = stages.filter((s) => s.indices.length > 0);

  return (
    <div className="playbook-flow playbook-flow--phased" style={{ padding: '16px 8px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      {activeStages.map((stage, stageIdx) => (
        <div
          key={stage.title}
          style={{
            background: 'var(--bg-panel)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: 16,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--text-muted)',
              marginBottom: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span>{stage.title}</span>
            <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)' }}>
              ({stage.indices.length} {stage.indices.length === 1 ? 'step' : 'steps'})
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
            {stage.indices.map((index) => {
              const step = steps[index];
              if (!step) return null;
              const result = stepResults?.[index];
              const isSelected = selectedStepIndex === index;
              const isRunning = activeRunningIndex === index;

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => onSelectStep(index)}
                  aria-pressed={isSelected}
                  style={{
                    textAlign: 'left',
                    background: isSelected ? 'var(--bg-panel)' : 'var(--bg-inset)',
                    border: `1.5px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
                    borderRadius: 6,
                    padding: '10px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    boxShadow: isSelected ? '0 0 0 2px rgba(59, 130, 246, 0.25)' : undefined,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>
                        #{index + 1}
                      </span>
                      <StepTypePill type={step.type} />
                    </div>
                    {result ? (
                      <StepStatusPill status={result.status} />
                    ) : isRunning ? (
                      <span
                        style={{
                          fontSize: 11,
                          padding: '1px 6px',
                          borderRadius: 9999,
                          background: '#1d4ed8',
                          color: '#fff',
                        }}
                      >
                        running
                      </span>
                    ) : null}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>
                    {step.description}
                  </div>
                  {step.target ? (
                    <div style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                      {step.target}
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          {stageIdx < activeStages.length - 1 ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12, color: 'var(--border, #64748b)' }}>
              ▼
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// --------------------------------------------------------------------------------
// VARIANT D: Compact Auto-Wrap Grid Flow (Zero-Scroll Bento Pipeline)
// --------------------------------------------------------------------------------
export function VariantD_CompactGridFlow({
  steps,
  stepResults,
  selectedStepIndex,
  onSelectStep,
  activeRunningIndex,
}: PlaybookFlowProps): JSX.Element {
  if (steps.length === 0) {
    return <div className="text-muted p-4 text-center">No steps in this plan.</div>;
  }

  return (
    <div
      className="playbook-flow playbook-flow--compact-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
        gap: 10,
        padding: '10px 4px',
      }}
    >
      {steps.map((step, index) => {
        const result = stepResults?.[index];
        const isSelected = selectedStepIndex === index;
        const isRunning = activeRunningIndex === index;
        const isLast = index === steps.length - 1;
        const status = isRunning ? 'running' : result?.status ?? 'pending';

        const statusColor =
          status === 'passed'
            ? '#10b981'
            : status === 'failed'
              ? '#ef4444'
              : status === 'running'
                ? '#3b82f6'
                : 'var(--border, #475569)';

        return (
          <button
            key={index}
            type="button"
            onClick={() => onSelectStep(index)}
            aria-pressed={isSelected}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              textAlign: 'left',
              background: isSelected ? 'var(--bg-panel)' : 'var(--bg-inset)',
              border: `1.5px solid ${isSelected ? '#3b82f6' : 'var(--border)'}`,
              borderTop: `3px solid ${statusColor}`,
              borderRadius: 6,
              padding: '8px 10px',
              cursor: 'pointer',
              position: 'relative',
              boxShadow: isSelected
                ? '0 0 0 2px rgba(59, 130, 246, 0.3)'
                : '0 1px 3px rgba(0,0,0,0.06)',
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span
                  style={{
                    fontSize: 10.5,
                    fontWeight: 800,
                    color: 'var(--text-muted)',
                    background: 'rgba(148, 163, 184, 0.1)',
                    padding: '1px 5px',
                    borderRadius: 3,
                  }}
                >
                  #{index + 1}
                </span>
                <StepTypePill type={step.type} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                {result ? (
                  <StepStatusPill status={result.status} />
                ) : isRunning ? (
                  <span
                    style={{
                      fontSize: 10,
                      padding: '1px 5px',
                      borderRadius: 9999,
                      background: '#1d4ed8',
                      color: '#fff',
                    }}
                  >
                    run
                  </span>
                ) : null}
                {!isLast ? (
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }} title="Next step">
                    →
                  </span>
                ) : null}
              </div>
            </div>

            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                lineHeight: 1.3,
                color: 'var(--text)',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
              title={step.description}
            >
              {step.description}
            </div>

            {step.target ? (
              <div
                style={{
                  fontSize: 10.5,
                  fontFamily: 'monospace',
                  color: 'var(--text-muted)',
                  background: 'var(--bg-inset)',
                  padding: '2px 4px',
                  borderRadius: 3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={step.target}
              >
                {step.target}
              </div>
            ) : null}

            {result?.durationMs !== undefined ? (
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 'auto', alignSelf: 'flex-end' }}>
                {formatDuration(result.durationMs)}
              </div>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
